import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

export default function EmailPasswordForm({ mode = 'login' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithEmail, registerWithEmail, error } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  const isRegister = mode === 'register';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError(null);
    setLoading(true);

    try {
      if (isRegister) {
        const result = await registerWithEmail(email, password, name);
        if (result.success) {
          if (result.isNewUser) {
            navigate('/onboarding');
          } else {
            navigate('/');
          }
        } else {
          setLocalError(result.error);
        }
      } else {
        const result = await loginWithEmail(email, password);
        if (result.success) {
          const from = location.state?.from?.pathname || '/';
          navigate(from);
        } else {
          if (result.provider) {
            setLocalError(`This account uses ${result.provider} sign-in. Please use that method.`);
          } else {
            setLocalError(result.error);
          }
        }
      }
    } catch (err) {
      setLocalError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const displayError = localError || error;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {displayError && (
        <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
          {displayError}
        </div>
      )}

      {isRegister && (
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:border-transparent"
            style={{ '--tw-ring-color': '#CC0202' }}
            placeholder="John Doe"
            required={isRegister}
          />
        </div>
      )}

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:border-transparent"
          style={{ '--tw-ring-color': '#CC0202' }}
          placeholder="you@example.com"
          required
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
            Password
          </label>
          {!isRegister && (
            <Link
              to="/forgot-password"
              className="text-sm hover:underline"
              style={{ color: '#CC0202' }}
            >
              Forgot password?
            </Link>
          )}
        </div>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:border-transparent"
            style={{ '--tw-ring-color': '#CC0202' }}
            placeholder={isRegister ? 'Min 8 characters' : 'Enter your password'}
            minLength={isRegister ? 8 : undefined}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {isRegister && (
          <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters</p>
        )}
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
        style={{ backgroundColor: '#CC0202' }}
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {isRegister ? 'Create Account' : 'Sign In'}
      </button>
    </form>
  );
}
